/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorInfo } from '../../../../api/model';
import { usePostSectionPreview } from '../../../../api/sections/sections';
import { useDefaultOptions } from '../../../../hooks/api';
import '../../../../styles/Dropdown.css';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  sectionId: number;
  isReadOnlySection: boolean;
  isShowPreviewWebOm: boolean; // navi, 翻訳navi 以外でWebOmプレビューを表示
}

/* 原稿編集画面 PDFプレビュー用プルダウン */
function PreviewCommand({ sectionId, isReadOnlySection, isShowPreviewWebOm }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);

  // #region プレビュー

  // プレビューAPI
  const { data, mutateAsync, isSuccess, isLoading, isError, error } = usePostSectionPreview<
    AxiosError<ErrorInfo>,
    unknown
  >(options);
  // プレビュー確認
  const [displayConfirmPreview, setDisplayConfirmPreview] = useState(false);
  // プレビューエラー
  const [displayPreviewError, setDisplayPreviewError] = useState(false);
  // プレビュー実行
  const handlePreview = async (newValue: { value: number } | null) => {
    if (newValue != null) {
      await mutateAsync({ data: { document_type: newValue.value, section_id: sectionId } });
    }
  };
  // 成功すると画面を表示
  useEffect(() => {
    if (isSuccess) {
      window.open(data.data.url);
    }
  }, [isSuccess, data?.data.url]);
  // 失敗
  useEffect(() => {
    setDisplayPreviewError(isError);
  }, [isError]);
  // #endregion

  // #region UI制御

  // 選択されたプルダウン
  const [selectValue, setSelectValue] = useState<{ value: number } | null>(null);

  // プルダウンの表示
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownListRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const handleClickToCloseDropdown = (event: any) => {
      const element = dropdownListRef.current;
      if (!showDropdown || element?.contains(event.target)) return;
      setShowDropdown(false);
    };
    window.addEventListener('click', handleClickToCloseDropdown);
    return () => {
      window.removeEventListener('click', handleClickToCloseDropdown);
    };
  }, [showDropdown, dropdownListRef]);
  // #endregion

  return (
    <div className="dropdowncontainer">
      <span
        className="dropdownopen"
        onClick={() => !isReadOnlySection && setShowDropdown((prevState) => !prevState)}
        ref={dropdownListRef}
        style={{
          width: '100px',
          color: isReadOnlySection ? '#9d9d9d' : '',
          cursor: isReadOnlySection ? 'default' : 'pointer',
        }}
      >
        <i
          className="fa-solid fa-eye"
          style={{
            color: isReadOnlySection ? '#9d9d9d' : '',
            cursor: isReadOnlySection ? 'default' : 'pointer',
          }}
        />
        {t('プレビュー')}
      </span>
      {showDropdown && (
        <ul className="dropdownlist" style={{ width: '100px' }}>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 10 });
              setDisplayConfirmPreview(true);
            }}
          >
            {t('PDF')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 13 });
              setDisplayConfirmPreview(true);
            }}
          >
            {t('PDF(88%)')}
          </li>
          {isShowPreviewWebOm && (
            <li
              className="dropdownitem"
              onClick={() => {
                setSelectValue({ value: 30 });
                setDisplayConfirmPreview(true);
              }}
            >
              {t('WebOM')}
            </li>
          )}
        </ul>
      )}
      {/* スピナーモーダル */}
      {isLoading && <SpinnerModal />}
      {/* プレビューエラー */}
      {displayPreviewError && <ErrorModal error={error} onClickOk={() => setDisplayPreviewError(false)} />}
      {/* プレビュー確認 */}
      {displayConfirmPreview && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmPreview(false)}
          onClickOk={async () => {
            setDisplayConfirmPreview(false);
            await handlePreview(selectValue);
          }}
        >
          {t('プレビューしますか？')}
        </ConfirmModal>
      )}
    </div>
  );
}
export default PreviewCommand;
